import React, { useState, useRef, useEffect } from 'react'

import { Mic, Square } from 'lucide-react'

import { elevenLabsService } from '@/core/api/services/elevenLabsService'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { AudioPlayer } from './AudioPlayer'

interface RecordAudioProps {
	onRecordingComplete: (audioBlob: Blob) => void
	onProcessSpeech: (audioBlob: Blob) => Promise<void>
	shouldProcessRecording?: boolean
}

export const RecordAudio: React.FC<RecordAudioProps> = ({
	onProcessSpeech,
	shouldProcessRecording = false,
}) => {
	const { t } = useTranslation('campaigns')
	const { showNotification } = useNotificationContext()
	const [isRecording, setIsRecording] = useState(false)
	const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null)
	const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null)
	const [recordingTime, setRecordingTime] = useState(0)
	const [isProcessing, setIsProcessing] = useState(false)

	const mediaRecorderRef = useRef<MediaRecorder | null>(null)
	const audioChunksRef = useRef<Blob[]>([])
	const timerIntervalRef = useRef<NodeJS.Timeout | null>(null)

	const startRecording = async () => {
		try {
			audioChunksRef.current = []
			setRecordingTime(0)

			if (!navigator.mediaDevices?.getUserMedia) {
				const isSecure =
					typeof window !== 'undefined' ? window.isSecureContext : true

				showNotification({
					type: 'error',
					message: t('ai.record.notifications.microphoneUnavailableTitle'),
					description: (
						<div className="space-y-1">
							<div>
								{t('ai.record.notifications.microphoneUnavailableDesc')}
							</div>
							{!isSecure && (
								<div>
									{t('ai.record.notifications.secureConnectionRequired')}
								</div>
							)}
							<div>
								{t('ai.record.notifications.microphoneUnavailableAction')}
							</div>
						</div>
					),
				})
				return
			}

			if (
				typeof window === 'undefined' ||
				typeof MediaRecorder === 'undefined'
			) {
				showNotification({
					type: 'error',
					message: t('ai.record.notifications.recordingNotSupportedTitle'),
					description: t('ai.record.notifications.recordingNotSupportedDesc'),
				})
				return
			}

			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

			// iOS Safari/WebViews are picky about recording mime types.
			// Prefer mp4 on iOS, fall back to webm/ogg where supported.
			const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
			const isIOS = /iPad|iPhone|iPod/.test(ua)
			const candidates = isIOS
				? ['audio/mp4', 'audio/aac']
				: ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']

			const chosenMimeType = candidates.find(
				(type) => type && MediaRecorder.isTypeSupported?.(type)
			)

			mediaRecorderRef.current = new MediaRecorder(
				stream,
				chosenMimeType ? { mimeType: chosenMimeType } : undefined
			)

			mediaRecorderRef.current.ondataavailable = (event) => {
				audioChunksRef.current.push(event.data)
			}

			mediaRecorderRef.current.onstop = () => {
				const mimeType = mediaRecorderRef.current?.mimeType || ''
				const audioBlob = new Blob(audioChunksRef.current, {
					type: mimeType,
				})
				setRecordedAudio(audioBlob)
			}

			mediaRecorderRef.current.start()
			setIsRecording(true)

			timerIntervalRef.current = setInterval(() => {
				setRecordingTime((prev) => {
					const newTime = prev + 1
					if (newTime >= 120) {
						if (
							mediaRecorderRef.current &&
							mediaRecorderRef.current.state === 'recording'
						) {
							mediaRecorderRef.current.stop()
							mediaRecorderRef.current.stream
								.getTracks()
								.forEach((track) => track.stop())
							setIsRecording(false)
							if (timerIntervalRef.current) {
								clearInterval(timerIntervalRef.current)
							}
						}
					}
					return newTime
				})
			}, 1000)
		} catch (error) {
			console.error('Error accessing microphone:', error)
			showNotification({
				type: 'error',
				message: t('ai.record.notifications.couldNotStartRecordingTitle'),
				description:
					error instanceof Error
						? error.message
						: t('ai.record.notifications.couldNotStartRecordingDesc'),
			})
		}
	}

	const stopRecording = () => {
		if (mediaRecorderRef.current && isRecording) {
			mediaRecorderRef.current.stop()
			mediaRecorderRef.current.stream
				.getTracks()
				.forEach((track) => track.stop())
			setIsRecording(false)

			if (timerIntervalRef.current) {
				clearInterval(timerIntervalRef.current)
			}
		}
	}

	const toggleRecording = async () => {
		if (!isRecording) {
			await startRecording()
		} else {
			stopRecording()
		}
	}

	const clearRecording = () => {
		setRecordedAudio(null)
		setRecordingTime(0)
	}

	const handleProcessSpeech = async () => {
		if (!recordedAudio) return

		setIsProcessing(true)
		try {
			await onProcessSpeech(recordedAudio)
		} catch (error) {
			console.error('Error processing speech:', error)
		} finally {
			setIsProcessing(false)
		}
	}

	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60)
		const secs = seconds % 60
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}

	useEffect(() => {
		if (!recordedAudio) {
			setRecordedAudioUrl(null)
			return
		}

		const url = elevenLabsService.createAudioUrl(recordedAudio)
		setRecordedAudioUrl(url)

		return () => {
			URL.revokeObjectURL(url)
		}
	}, [recordedAudio])

	useEffect(() => {
		if (shouldProcessRecording && recordedAudio && !isProcessing) {
			handleProcessSpeech().catch((error) => {
				console.error('Error in handleProcessSpeech:', error)
			})
		}
	}, [shouldProcessRecording, recordedAudio, isProcessing])

	return (
		<div className="w-full h-full flex flex-col">
			{!recordedAudio ? (
				<div className="relative overflow-hidden rounded-lg bg-card border border-main-accent/80 p-2 md:p-14 flex-1 flex flex-col justify-center items-center">
					<div
						className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
						style={{
							width: '900px',
							height: '300px',
							background:
								'radial-gradient(ellipse at center, oklch(0.5808 0.2364 284.77 / 0.4) 0%, oklch(0.5808 0.2364 284.77 / 0.2) 30%, transparent 70%)',
						}}
					/>

					<div className="relative flex flex-row-reverse gap-4 md:gap-0 md:flex-col items-center justify-start md:justify-center md:space-y-4">
						{isRecording && (
							<div className="flex justify-center">
								<div
									className="h-2 w-2 rounded-full bg-red-600"
									style={{
										animation:
											'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
									}}
								/>
							</div>
						)}

						<div className="md:text-center md:space-y-2">
							{isRecording ? (
								<h3 className="text-sm  font-medium">
									{t('ai.record.recording')}
								</h3>
							) : (
								<>
									<h3 className="text-sm ">{t('ai.record.instruction')}</h3>
									<p className="text-xs ">{t('ai.record.limit')}</p>
								</>
							)}
						</div>

						<button
							onClick={toggleRecording}
							className="h-8 w-8 md:h-12 md:w-12 cursor-pointer rounded-full flex items-center justify-center transition-all bg-white hover:bg-white/90 text-black shadow-lg"
						>
							{isRecording ? (
								<Square className="h-5 w-5 fill-current" />
							) : (
								<Mic className="h-5 w-5" />
							)}
						</button>

						{isRecording && (
							<div className="text-center">
								<p className="text-sm font-mono text-white font-semibold">
									{formatTime(recordingTime)} / 2:00
								</p>
							</div>
						)}
					</div>
				</div>
			) : recordedAudioUrl ? (
				<AudioPlayer
					audioUrl={recordedAudioUrl}
					audioBlob={recordedAudio}
					fileName="recorded_audio.mp3"
					onDownload={() => {
						elevenLabsService.downloadAudio(recordedAudio, 'recorded_audio.mp3')
					}}
					onDelete={clearRecording}
					isRecording={true}
				/>
			) : null}
		</div>
	)
}
